import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import CandlestickChart from '../components/CandlestickChart';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, subYears } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  Search,
  BarChart3,
  ArrowRight,
  Loader2,
  Check,
  ChevronsUpDown,
  CandlestickChart as CandleIcon,
  CalendarIcon,
  TrendingUp,
  AlertTriangle,
  Video,
  MoveRight,
} from 'lucide-react';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${BACKEND_URL}/api`;
const MIN_PATTERN_DAYS = 90;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const [symbols, setSymbols] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [startDate, setStartDate] = useState(subYears(new Date(), 1));
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [quickStats, setQuickStats] = useState(null);
  const [symbolSearchOpen, setSymbolSearchOpen] = useState(false);
  const [symbolSearch, setSymbolSearch] = useState('');
  const [topMovers, setTopMovers] = useState([]);
  const [topMoversLoading, setTopMoversLoading] = useState(false);
  const [rangeWarning, setRangeWarning] = useState('');
  const [videoModal, setVideoModal] = useState({ open: false, title: '', src: '' });

  const openVideo = (title, src) => setVideoModal({ open: true, title, src });
  const closeVideo = () => setVideoModal({ open: false, title: '', src: '' });

  const VIDEO_PATHS = {
    future: '/videos/hissemin-gelecegi.MP4',
    rising: '/videos/yukselecek-hisse.MP4',
  };
  
  // Candlestick chart states
  const [candleData, setCandleData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [timeInterval, setTimeInterval] = useState('1wk');
  const [chartPeriod, setChartPeriod] = useState('7y');
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchSymbols();
    fetchTopMovers();
  }, []);

  // Filtrelenmiş semboller (yazılan metne göre)
  const filteredSymbols = useMemo(() => {
    if (!symbolSearch) return symbols;
    const search = symbolSearch.toUpperCase();
    return symbols.filter(s => s.startsWith(search) || s.includes(search));
  }, [symbols, symbolSearch]);

  const fetchSymbols = async () => {
    try {
      const response = await axios.get(`${API_URL}/stocks/symbols`, {
        headers: getAuthHeader()
      });
      setSymbols(response.data.symbols);
    } catch (error) {
      toast.error('Semboller yüklenemedi');
    }
  };

  const fetchTopMovers = async () => {
    setTopMoversLoading(true);
    try {
      const response = await axios.get(`${API_URL}/stocks/top-movers?limit=8`, {
        headers: getAuthHeader(),
      });
      setTopMovers(response.data || []);
    } catch (error) {
      console.error('Top movers error:', error);
      setTopMovers([]);
    } finally {
      setTopMoversLoading(false);
    }
  };

  const getRangeDayCount = (start, end) => {
    if (!start || !end) return 0;
    const startMidnight = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endMidnight = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    return Math.floor((endMidnight - startMidnight) / (24 * 60 * 60 * 1000)) + 1;
  };

  const selectedRangeDays = useMemo(
    () => getRangeDayCount(startDate, endDate),
    [startDate, endDate]
  );

  const isRangeValid = isAdmin || selectedRangeDays >= MIN_PATTERN_DAYS;

  const applyDateRange = useCallback((nextStart, nextEnd, source = 'manual') => {
    setStartDate(nextStart);
    setEndDate(nextEnd);

    if (isAdmin) {
      setRangeWarning('');
      return;
    }

    const dayCount = getRangeDayCount(nextStart, nextEnd);
    if (dayCount < MIN_PATTERN_DAYS) {
      const warning = `En az ${MIN_PATTERN_DAYS} günlük kalıp seçmelisiniz (şu an ${Math.max(dayCount, 0)} gün).`;
      setRangeWarning(warning);
      if (source !== 'init') {
        toast.warning(warning);
      }
    } else {
      setRangeWarning('');
      if (source === 'chart') {
        toast.success(`Tarih aralığı seçildi: ${format(nextStart, 'dd MMM yyyy', { locale: tr })} - ${format(nextEnd, 'dd MMM yyyy', { locale: tr })}`);
      }
    }
  }, [isAdmin]);

  useEffect(() => {
    applyDateRange(startDate, endDate, 'init');
  }, [isAdmin, applyDateRange, startDate, endDate]);

  const fetchCandlestickData = async (symbol, interval = timeInterval, period = chartPeriod) => {
    if (!symbol) return;
    setChartLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/stocks/${symbol}/candlestick?interval=${interval}&period=${period}`,
        { headers: getAuthHeader() }
      );
      setCandleData(response.data.candles);
    } catch (error) {
      console.error('Candlestick error:', error);
      toast.error('Grafik verisi yüklenemedi');
    } finally {
      setChartLoading(false);
    }
  };

  const fetchQuickStats = async (symbol) => {
    if (!symbol) return;
    try {
      const response = await axios.get(`${API_URL}/stocks/${symbol}/quick`, {
        headers: getAuthHeader()
      });
      setQuickStats(response.data);
    } catch (error) {
      console.error('Quick stats error:', error);
    }
  };

  const handleSymbolChange = (value) => {
    setSelectedSymbol(value);
    fetchQuickStats(value);
    fetchCandlestickData(value);
  };

  const handleIntervalChange = (interval) => {
    setTimeInterval(interval);
    if (selectedSymbol) {
      fetchCandlestickData(selectedSymbol, interval, chartPeriod);
    }
  };

  const handlePeriodChange = (period) => {
    setChartPeriod(period);
    if (selectedSymbol) {
      fetchCandlestickData(selectedSymbol, timeInterval, period);
    }
  };

  const handleRangeSelect = useCallback((range) => {
    applyDateRange(range.start, range.end, 'chart');
  }, [applyDateRange]);

  const handleAnalyzeByMode = (mode = 'full') => {
    if (!selectedSymbol) {
      toast.error('Lütfen bir hisse seçin');
      return;
    }

    if (!isRangeValid) {
      toast.error(`Analiz için en az ${MIN_PATTERN_DAYS} günlük kalıp seçmelisiniz.`);
      return;
    }
    
    navigate('/analysis', {
      state: {
        symbol: selectedSymbol,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        initialMode: mode,
        initialInterval: timeInterval,
      }
    });
  };

  return (
    <div className="min-h-screen bg-[#F6F1EA]" data-testid="dashboard-page">
      <Sidebar />
      
      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in">
            <h1 className="text-3xl md:text-4xl font-bold text-[#2E2620] font-['Playfair_Display']">
              Ana Sayfa
            </h1>
            <p className="text-[#7A6A5C] mt-2">
              BIST 100 hisselerinde benzer kalıpları keşfedin
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5 text-sm font-medium text-[#7A6A5C] select-none">
                <span>Nasıl Kullanılır?</span>
                <MoveRight className="w-4 h-4 text-[#C86F4A]" />
              </div>
              <button
                type="button"
                onClick={() => openVideo('Geçmişte Benzer Grafikler', VIDEO_PATHS.future)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#6D7C3B] text-[#6D7C3B] bg-white hover:bg-[#6D7C3B]/10 text-sm font-medium transition-colors"
                data-testid="video-show-future-btn"
              >
                Geçmişte Benzer Grafikler Videosu
                <Video className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => openVideo('Yükselecek Hisse Bul', VIDEO_PATHS.rising)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#C86F4A] text-[#C86F4A] bg-white hover:bg-[#C86F4A]/10 text-sm font-medium transition-colors"
                data-testid="video-find-rising-btn"
              >
                Yükselecek Hisse Bul Videosu
                <Video className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* <Card className="mb-6 border-[#F1B7B7] bg-[#FFF5F5]" data-testid="system-load-warning-card">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[#B04832]/10 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-[#B04832]" />
                </div>
                <p className="text-sm text-[#7A1F1F] leading-relaxed">
                  Sistemimiz, Borsa İstanbul hisselerinin geçmiş verilerini anlık olarak analiz ettiği için yoğun
                  dönemlerde eşzamanlı veri talepleri artabilir. Bu süreçte zaman zaman geçici hata almanız normaldir.
                  Artan talebi karşılamak için altyapı güncellemelerimiz devam etmektedir. Anlayışınız için teşekkür
                  ederiz.
                </p>
              </div>
            </CardContent>
          </Card> */}

          <div className="flex flex-col">
          <Card className="card-organic mb-6 order-2 md:order-1" data-testid="top-movers-card">
            <CardHeader>
              <CardTitle className="text-xl font-['Playfair_Display'] text-[#2E2620] flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#6D7C3B]" />
                Bu Ay En Çok Yükselen Hisseler
              </CardTitle>
              <CardDescription className="text-[#7A6A5C]">
                TradingView verisine göre son 1 ayda en güçlü yükseliş gösteren hisseler.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {topMoversLoading ? (
                <div className="py-4 flex items-center gap-2 text-[#7A6A5C] text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Bülten yükleniyor...
                </div>
              ) : topMovers.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {topMovers.map((item) => (
                    <div
                      key={item.symbol}
                      className="rounded-lg border border-[#E6DCCF] bg-white p-3"
                      data-testid={`top-mover-${item.symbol}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-[#2E2620]">{item.symbol}</span>
                        <span className="text-sm font-bold text-[#6D7C3B]">+{item.monthly_change_percent}%</span>
                      </div>
                      <p className="text-xs text-[#7A6A5C] mt-1">
                        Günlük: {typeof item.daily_change_percent === 'number'
                          ? `${item.daily_change_percent >= 0 ? '+' : ''}${item.daily_change_percent}%`
                          : '—'}
                      </p>
                      <p className="text-xs text-[#7A6A5C]">
                        Fiyat: {typeof item.current_price === 'number'
                          ? `₺${item.current_price.toLocaleString('tr-TR')}`
                          : '—'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#7A6A5C]">Bülten şu an getirilemiyor, lütfen daha sonra tekrar deneyin.</p>
              )}
            </CardContent>
          </Card>

          {/* Main Analysis Card */}
          <Card className="card-organic animate-slide-up order-1 md:order-2" data-testid="analysis-form-card">
            <CardHeader>
              <CardTitle className="text-2xl font-['Playfair_Display'] text-[#2E2620]">
                Yeni Analiz Başlat
              </CardTitle>
              <CardDescription className="text-[#7A6A5C]">
                Analiz etmek istediğiniz hisseyi ve tarih aralığını seçin
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Symbol Select - Searchable Combobox */}
                <div className="space-y-2">
                  <Label className="text-[#2E2620]">Hisse Senedi</Label>
                  <Popover open={symbolSearchOpen} onOpenChange={setSymbolSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={symbolSearchOpen}
                        className="w-full justify-between border-[#E6DCCF] hover:bg-[#E8D9C7]/50"
                        data-testid="symbol-select"
                      >
                        {selectedSymbol || "Hisse ara..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[calc(100vw-2rem)] max-w-[300px] p-0" align="start">
                      <Command>
                        <CommandInput 
                          placeholder="Hisse ara (örn: THY, GAR)..." 
                          value={symbolSearch}
                          onValueChange={setSymbolSearch}
                          data-testid="symbol-search-input"
                        />
                        <CommandList>
                          <CommandEmpty>Hisse bulunamadı.</CommandEmpty>
                          <CommandGroup className="max-h-60 overflow-auto">
                            {filteredSymbols.slice(0, 50).map((symbol) => (
                              <CommandItem
                                key={symbol}
                                value={symbol}
                                onSelect={() => {
                                  handleSymbolChange(symbol);
                                  setSymbolSearchOpen(false);
                                  setSymbolSearch('');
                                }}
                                data-testid={`symbol-option-${symbol}`}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedSymbol === symbol ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {symbol}
                              </CommandItem>
                            ))}
                            {filteredSymbols.length > 50 && (
                              <div className="p-2 text-xs text-center text-[#7A6A5C]">
                                +{filteredSymbols.length - 50} daha... (aramayı daraltın)
                              </div>
                            )}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Time Interval */}
                <div className="space-y-2">
                  <Label className="text-[#2E2620]">Zaman Dilimi</Label>
                  <Select value={timeInterval} onValueChange={handleIntervalChange}>
                    <SelectTrigger className="border-[#E6DCCF]" data-testid="interval-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1d">Günlük</SelectItem>
                      <SelectItem value="1wk">Haftalık</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Period */}
                <div className="space-y-2">
                  <Label className="text-[#2E2620]">Periyot</Label>
                  <Select value={chartPeriod} onValueChange={handlePeriodChange}>
                    <SelectTrigger className="border-[#E6DCCF]" data-testid="period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3mo">3 Ay</SelectItem>
                      <SelectItem value="6mo">6 Ay</SelectItem>
                      <SelectItem value="1y">1 Yıl</SelectItem>
                      <SelectItem value="2y">2 Yıl</SelectItem>
                      <SelectItem value="7y">7 Yıl</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Candlestick Chart */}
              {selectedSymbol && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <CandleIcon className="w-5 h-5 text-[#C86F4A]" />
                      <h3 className="font-medium text-[#2E2620]">{selectedSymbol} Grafiği</h3>
                      {quickStats && (
                        <span className={`text-sm font-medium ${quickStats.change_percent >= 0 ? 'text-[#6D7C3B]' : 'text-[#B04832]'}`}>
                          ₺{quickStats.current_price?.toLocaleString('tr-TR')} ({quickStats.change_percent >= 0 ? '+' : ''}{quickStats.change_percent?.toFixed(2)}%)
                        </span>
                      )}
                    </div>
                  </div>
                  <CandlestickChart
                    data={candleData}
                    loading={chartLoading}
                    onRangeSelect={handleRangeSelect}
                    height={350}
                  />
                </div>
              )}

              {/* Date Selection - Manuel veya Grafikten */}
              <div className="mt-6 p-4 bg-[#F6F1EA]/50 rounded-xl border border-[#E6DCCF]">
                <div className="flex items-center gap-2 mb-4">
                  <CalendarIcon className="w-4 h-4 text-[#C86F4A]" />
                  <span className="text-sm font-medium text-[#2E2620]">Analiz Tarih Aralığı</span>
                  <span className="text-xs text-[#A89F91]">(grafikten seçin veya manuel girin)</span>
                </div>
                
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    {/* Start Date Picker */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-[140px] sm:w-[160px] justify-start text-left font-normal border-[#E6DCCF] hover:bg-white bg-white"
                          data-testid="start-date-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-[#6D7C3B]" />
                          <div>
                            <span className="text-xs text-[#7A6A5C] block">Başlangıç</span>
                            <span className="text-sm font-medium text-[#2E2620]">
                              {format(startDate, 'dd MMM yyyy', { locale: tr })}
                            </span>
                          </div>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          locale={tr}
                          selected={startDate}
                          onSelect={(date) => {
                            if (!date) return;
                            applyDateRange(date, endDate, 'manual');
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    
                    <ArrowRight className="w-4 h-4 text-[#7A6A5C]" />
                    
                    {/* End Date Picker */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-[140px] sm:w-[160px] justify-start text-left font-normal border-[#E6DCCF] hover:bg-white bg-white"
                          data-testid="end-date-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-[#B04832]" />
                          <div>
                            <span className="text-xs text-[#7A6A5C] block">Bitiş</span>
                            <span className="text-sm font-medium text-[#2E2620]">
                              {format(endDate, 'dd MMM yyyy', { locale: tr })}
                            </span>
                          </div>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          locale={tr}
                          selected={endDate}
                          onSelect={(date) => {
                            if (!date) return;
                            applyDateRange(startDate, date, 'manual');
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      onClick={() => handleAnalyzeByMode('partial')}
                      disabled={!selectedSymbol || loading || !isRangeValid}
                      className="bg-[#C86F4A] hover:bg-[#B05D3A] text-white rounded-full px-5"
                      data-testid="predict-rising-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Search className="w-4 h-4 mr-2" />
                      )}
                      Yükselecek Hisse Bul
                    </Button>
                    <Button
                      onClick={() => handleAnalyzeByMode('full')}
                      disabled={!selectedSymbol || loading || !isRangeValid}
                      variant="outline"
                      className="border-[#6D7C3B] text-[#6D7C3B] hover:bg-[#6D7C3B]/10 rounded-full px-5"
                      data-testid="show-future-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Search className="w-4 h-4 mr-2" />
                      )}
                      Geçmişte benzer grafikleri göster
                    </Button>
                  </div>
                </div>
                {!isAdmin && (
                  <p className={`mt-3 text-xs ${isRangeValid ? 'text-[#6D7C3B]' : 'text-[#B04832]'}`}>
                    {isRangeValid
                      ? `Seçilen kalıp süresi: ${selectedRangeDays} gün (analiz için uygun).`
                      : (rangeWarning || `En az ${MIN_PATTERN_DAYS} günlük kalıp seçmelisiniz (şu an ${Math.max(selectedRangeDays, 0)} gün).`)}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
          </div>

          {/* Feature Cards */}
          <div className={`grid grid-cols-1 ${isAdmin ? 'md:grid-cols-2' : ''} gap-6 mt-8`}>
            {isAdmin && (
              <Card 
                className="card-organic card-interactive animate-slide-up stagger-3"
                onClick={() => navigate('/custom-pattern')}
                data-testid="custom-pattern-card"
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-[#2E2620] font-['Playfair_Display'] mb-2">
                        Özel Kalıp Araması
                      </h3>
                      <p className="text-[#7A6A5C] text-sm mb-4">
                        Kendi kriterlerinizi girerek özel dip-tepe kalıplarına uyan hisseleri bulun
                      </p>
                      <div className="flex items-center text-[#C86F4A] text-sm font-medium">
                        <span>Özel arama yap</span>
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </div>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-[#C86F4A]/10 flex items-center justify-center flex-shrink-0">
                      <Search className="w-6 h-6 text-[#C86F4A]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card 
              className="card-organic card-interactive animate-slide-up stagger-4"
              onClick={() => navigate('/saved')}
              data-testid="saved-analyses-card"
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-[#2E2620] font-['Playfair_Display'] mb-2">
                      Kayıtlı Analizler
                    </h3>
                    <p className="text-[#7A6A5C] text-sm mb-4">
                      Daha önce yaptığınız analizlere tekrar erişin ve karşılaştırın
                    </p>
                    <div className="flex items-center text-[#6D7C3B] text-sm font-medium">
                      <span>Analizleri görüntüle</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#6D7C3B]/10 flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-6 h-6 text-[#6D7C3B]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Dialog open={videoModal.open} onOpenChange={closeVideo}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-3xl bg-[#1a1a1a] border-[#333] p-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5 pb-3">
            <DialogTitle className="text-white font-[‘Playfair_Display’] flex items-center gap-2">
              <Video className="w-4 h-4 text-[#C86F4A]" />
              {videoModal.title}
            </DialogTitle>
            <DialogDescription className="text-[#999] text-xs">
              Nasıl kullanılır? &mdash; Kullanım videosu
            </DialogDescription>
          </DialogHeader>
          <div className="px-5 pb-5">
            {videoModal.open && videoModal.src && (
              <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
                <video
                  key={videoModal.src}
                  src={videoModal.src}
                  controls
                  autoPlay
                  className="w-full h-full"
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
